import React, { useState, useEffect } from 'react';
import { AuthProvider, useAuth } from './hooks/useAuth';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import ModuleView from './components/ModuleView';
import ChapterView from './components/ChapterView';
import AdminPanel from './components/admin/AdminPanel';
import Navbar from './components/Navbar';
import ProfilePage from './components/ProfilePage';
import FirstTimePasswordSetup from './components/FirstTimePasswordSetup';
import { AlertTriangle, Trophy, Settings, BarChart3, Users } from 'lucide-react';

// Main App Router Component (THIS MUST BE INSIDE AuthProvider)
const AppRouter = () => {
  const { isAuthenticated, loading, user, isFirstLogin, error } = useAuth();
  const [currentView, setCurrentView] = useState('dashboard');
  const [viewData, setViewData] = useState({});

  // CRITICAL FIX: Reset view to dashboard when user becomes authenticated
  useEffect(() => {
    if (isAuthenticated && user && !isFirstLogin) {
      console.log('✅ User authenticated, ensuring dashboard view');
      setCurrentView('dashboard');
      setViewData({});
    }
  }, [isAuthenticated, user, isFirstLogin]);

  // Navigation handler
  const handleNavigate = (view, data = {}) => {
    console.log('🧭 Navigating to:', view, data);
    setCurrentView(view);
    setViewData(data);
  };

  // Handle back navigation
  const handleBack = () => {
    console.log('⬅️ Navigating back from:', currentView);
    if (currentView === 'chapter') {
      setCurrentView('module');
    } else if (currentView === 'module' || currentView === 'admin') {
      setCurrentView('dashboard');
    } else {
      setCurrentView('dashboard');
    }
  };

  // Debug logging for authentication state
  useEffect(() => {
    console.log('🔍 App Router State:', {
      isAuthenticated,
      loading,
      isFirstLogin,
      userExists: !!user,
      userName: user?.username,
      userIsAdmin: user?.is_admin,
      currentView,
      error: error || 'none'
    });
  }, [isAuthenticated, loading, isFirstLogin, user, error, currentView]);

  // Show loading spinner while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando aplicación...</p>
        </div>
      </div>
    );
  }

  // Show login if not authenticated
  if (!isAuthenticated) {
    console.log('🔓 User not authenticated, showing login');
    return <Login />;
  }

  // CRITICAL: Check for first-time login AFTER authentication is confirmed
  if (isFirstLogin || (user && user.is_first_login)) {
    console.log('🔑 First time login detected, showing password setup');
    return <FirstTimePasswordSetup />;
  }

  // Additional safety check - if user exists but doesn't have proper authentication state
  if (!user) {
    console.warn('⚠️ Authenticated but no user data, forcing re-authentication');
    return <Login />;
  }

  // Show error state if there's a critical error
  if (error && !user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-6 max-w-md w-full text-center">
          <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Error de Autenticación</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary-dark transition-colors"
          >
            Recargar Página
          </button>
        </div>
      </div>
    );
  }

  console.log('✅ User authenticated and ready, showing main app');

  // Main application view with improved routing
  const renderCurrentView = () => {
    // CRITICAL: Check admin permissions for admin views
    const isAdminView = ['admin', 'analytics', 'users'].includes(currentView);
    
    if (isAdminView && !user?.is_admin) {
      console.warn('⚠️ Non-admin user trying to access admin view, redirecting to dashboard');
      // Auto-redirect non-admin users to dashboard
      setTimeout(() => {
        setCurrentView('dashboard');
        setViewData({});
      }, 100);
      
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-lg p-6 max-w-md w-full text-center">
            <AlertTriangle className="w-12 h-12 text-orange-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Acceso Denegado</h2>
            <p className="text-gray-600 mb-4">No tienes permisos de administrador para acceder a esta página.</p>
            <button 
              onClick={() => {
                setCurrentView('dashboard');
                setViewData({});
              }}
              className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary-dark transition-colors"
            >
              Ir al Dashboard
            </button>
          </div>
        </div>
      );
    }

    switch (currentView) {
      case 'module':
        return <ModuleView module={viewData.module} onNavigate={handleNavigate} onBack={handleBack} />;
      case 'chapter':
        return <ChapterView chapter={viewData.chapter} module={viewData.module} onBack={handleBack} />;
      case 'admin':
        return user?.is_admin ? <AdminPanel onBack={handleBack} /> : null;
      case 'profile':
        return <ProfilePage onBack={handleBack} />;
      case 'analytics':
        return user?.is_admin ? (
          <div className="container mx-auto px-4 py-8">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center mb-6">
                <BarChart3 className="w-6 h-6 text-primary mr-3" />
                <h1 className="text-2xl font-bold text-gray-900">Analíticas</h1>
              </div>
              <div className="text-center py-12 text-gray-500">
                <BarChart3 className="w-16 h-16 mx-auto mb-4 opacity-50" />
                <p>Panel de analíticas en desarrollo</p>
              </div>
            </div>
          </div>
        ) : null;
      case 'users':
        return user?.is_admin ? (
          <div className="container mx-auto px-4 py-8">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center mb-6">
                <Users className="w-6 h-6 text-primary mr-3" />
                <h1 className="text-2xl font-bold text-gray-900">Gestión de Usuarios</h1>
              </div>
              <div className="text-center py-12 text-gray-500">
                <Users className="w-16 h-16 mx-auto mb-4 opacity-50" />
                <p>Panel de usuarios en desarrollo</p>
              </div>
            </div>
          </div>
        ) : null;
      default:
        return <Dashboard onNavigate={handleNavigate} />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation Bar - shown for most views except module/chapter */}
      {!['module', 'chapter'].includes(currentView) && (
        <Navbar 
          user={user} 
          currentView={currentView} 
          onNavigate={handleNavigate} 
        />
      )}
      
      {/* Main Content */}
      <main className={!['module', 'chapter'].includes(currentView) ? '' : ''}>
        {renderCurrentView()}
      </main>
    </div>
  );
};

// Main App Component with AuthProvider wrapper
function App() {
  return (
    <AuthProvider>
      <AppRouter />
    </AuthProvider>
  );
}

export default App;